import { type FC, useMemo } from 'react';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import { IconDownload, IconEye, IconLoader2 } from '@tabler/icons-react';
import {
  ButtonField,
  ButtonFieldIcon,
  Tooltip as TooltipField,
  TooltipBody,
} from '@wk/components-react16';

import type { SiteComplianceEvaluation } from '@/domains/siteCompliance';
import { cn } from '@/lib/utils';
import { ORR_PLATFORMS } from '@/config/constants';
import { AutomationPlatform } from '@/domains/evaluation/evaluation.types';

import {
  formatSiteDetailEvaluationGeneratedAt,
  getSiteDetailScoreIndicatorColorClass,
} from './SiteDetailPage.utils';

const renderEvaluationSource = (platform: string | null) => {
  const entry =
    ORR_PLATFORMS[(platform ?? '') as AutomationPlatform] ??
    ORR_PLATFORMS[AutomationPlatform.UNKNOWN];
  const Icon = entry.icon;

  return (
    <span className="flex items-center gap-2">
      <span className="h-5 w-5 shrink-0">
        <Icon className="h-full w-full" />
      </span>
      <span>{entry.label}</span>
    </span>
  );
};

interface SiteDetailHistoryProps {
  activeEvaluationId: string | null;
  errorMessage?: string | null;
  evaluations: SiteComplianceEvaluation[];
  hasNextPage?: boolean;
  hideHeader?: boolean;
  isExportingCsv: boolean;
  isExportingHistoryCsv: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  isMobileViewport: boolean;
  onDownloadEvaluation: (evaluationId: string) => void;
  onExportHistory: () => void;
  onLoadMore?: () => void;
  onViewEvaluation: (evaluationId: string) => void;
}

const SiteDetailHistory: FC<SiteDetailHistoryProps> = ({
  activeEvaluationId,
  errorMessage,
  evaluations,
  hasNextPage = false,
  hideHeader = false,
  isExportingCsv,
  isExportingHistoryCsv,
  isFetchingNextPage = false,
  isLoading = false,
  isMobileViewport,
  onDownloadEvaluation,
  onExportHistory,
  onLoadMore,
  onViewEvaluation,
}) => {
  const sanitizeEvaluationActionId = (value: string) =>
    value.replace(/[^a-zA-Z0-9-_]/g, '-');

  const columnDefs = useMemo<ColDef<SiteComplianceEvaluation>[]>(
    () => [
      {
        headerName: 'Generated At',
        field: 'generatedAt',
        minWidth: 150,
        flex: 1,
        cellRenderer: (
          params: CustomCellRendererProps<SiteComplianceEvaluation>,
        ) => {
          const evaluation = params.data;

          if (!evaluation) {
            return null;
          }

          return (
            <div className="flex items-center gap-2">
              <p>
                {formatSiteDetailEvaluationGeneratedAt(evaluation.generatedAt)}
              </p>
            </div>
          );
        },
      },
      {
        headerName: 'Source',
        field: 'platform',
        minWidth: 150,
        flex: 1,
        sortable: false,
        cellRenderer: (
          params: CustomCellRendererProps<SiteComplianceEvaluation>,
        ) => renderEvaluationSource(params.data?.platform ?? null),
      },
      {
        headerName: 'Score',
        field: 'score',
        minWidth: 150,
        flex: 1,
        cellRenderer: (
          params: CustomCellRendererProps<SiteComplianceEvaluation>,
        ) => {
          const evaluation = params.data;

          if (!evaluation) {
            return null;
          }

          return (
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-2 w-2 rounded-full',
                  getSiteDetailScoreIndicatorColorClass(evaluation.score),
                )}
              />
              <p>{evaluation.score}%</p>
            </div>
          );
        },
      },
      {
        headerName: 'Total Checkpoints',
        field: 'totalCheckpoints',
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: 'Actions',
        colId: 'actions',
        minWidth: 150,
        flex: 1,
        sortable: false,
        cellRenderer: (
          params: CustomCellRendererProps<SiteComplianceEvaluation>,
        ) => {
          const evaluation = params.data;

          if (!evaluation) {
            return null;
          }

          const sanitizedId = sanitizeEvaluationActionId(evaluation.id);
          const viewActionId = `view-site-evaluation-${sanitizedId}`;
          const exportActionId = `export-site-evaluation-${sanitizedId}`;

          return (
            <div className="flex h-full items-center justify-center gap-2">
              <TooltipField
                position="right"
                targetSelector={`#${viewActionId}`}
              >
                <TooltipBody slot="tooltipBody">View Evaluation</TooltipBody>
              </TooltipField>
              <ButtonFieldIcon size="small16px">
                <button
                  id={viewActionId}
                  type="button"
                  aria-label="View Evaluation"
                  onClick={() => onViewEvaluation(evaluation.id)}
                >
                  <IconEye size={18} />
                </button>
              </ButtonFieldIcon>
              <TooltipField
                position="right"
                targetSelector={`#${exportActionId}`}
              >
                <TooltipBody slot="tooltipBody">Download Report</TooltipBody>
              </TooltipField>
              <ButtonFieldIcon size="small16px">
                <button
                  id={exportActionId}
                  type="button"
                  aria-label="Download Report"
                  disabled={
                    isExportingCsv && activeEvaluationId === evaluation.id
                  }
                  onClick={() => onDownloadEvaluation(evaluation.id)}
                >
                  {isExportingCsv && activeEvaluationId === evaluation.id ? (
                    <IconLoader2 className="animate-spin" size={18} />
                  ) : (
                    <IconDownload size={18} />
                  )}
                </button>
              </ButtonFieldIcon>
            </div>
          );
        },
      },
    ],
    [
      activeEvaluationId,
      isExportingCsv,
      onDownloadEvaluation,
      onViewEvaluation,
    ],
  );

  if (errorMessage) {
    return (
      <section className="flex flex-col gap-3">
        {!hideHeader ? (
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-medium">Evaluation History</h2>
            <ButtonField mode="ghost" size="small">
              <button
                type="button"
                disabled={isExportingHistoryCsv}
                onClick={onExportHistory}
              >
                {isExportingHistoryCsv ? 'Exporting...' : 'Export history CSV'}
              </button>
            </ButtonField>
          </div>
        ) : null}
        <div className="mt-2 rounded border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
          {errorMessage}
        </div>
      </section>
    );
  }

  if (isLoading && !evaluations.length) {
    return (
      <section className="flex flex-col gap-3">
        {!hideHeader ? (
          <h2 className="text-sm font-medium">Evaluation History</h2>
        ) : null}
        <div className="flex min-h-[220px] items-center justify-center">
          <IconLoader2 className="animate-spin text-[#005b92]" size={24} />
        </div>
      </section>
    );
  }

  if (!evaluations.length) {
    return (
      <section className="flex flex-col gap-3">
        {!hideHeader ? (
          <h2 className="text-sm font-medium">Evaluation History</h2>
        ) : null}
        <p className="mt-2 text-sm text-gray-500">
          No published evaluations are available for this site.
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      {!hideHeader ? (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-medium">Evaluation History</h2>
            <p className="mt-1 text-sm text-gray-500">
              Latest-first history from the Site Compliance API.
            </p>
          </div>
          <ButtonField mode="ghost" size="small">
            <button
              type="button"
              disabled={isExportingHistoryCsv}
              onClick={onExportHistory}
            >
              {isExportingHistoryCsv ? 'Exporting...' : 'Export history CSV'}
            </button>
          </ButtonField>
        </div>
      ) : null}

      {isMobileViewport ? (
        <div className={hideHeader ? 'flex flex-col gap-3' : 'mt-4 flex flex-col gap-3'}>
          {evaluations.map((evaluation) => (
            <article
              key={evaluation.id}
              className="rounded-lg border bg-white p-3 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatSiteDetailEvaluationGeneratedAt(evaluation.generatedAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'h-2 w-2 rounded-full',
                      getSiteDetailScoreIndicatorColorClass(evaluation.score),
                    )}
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    {evaluation.score}%
                  </span>
                  <ButtonField mode="ghost" size="small">
                    <button
                      type="button"
                      aria-label="View evaluation"
                      onClick={() => onViewEvaluation(evaluation.id)}
                    >
                      <IconEye size={14} />
                    </button>
                  </ButtonField>
                  <ButtonField mode="ghost" size="small">
                    <button
                      type="button"
                      aria-label="Download report"
                      disabled={
                        isExportingCsv && activeEvaluationId === evaluation.id
                      }
                      onClick={() => onDownloadEvaluation(evaluation.id)}
                    >
                      {isExportingCsv && activeEvaluationId === evaluation.id ? (
                        <IconLoader2 className="animate-spin" size={14} />
                      ) : (
                        <IconDownload size={14} />
                      )}
                    </button>
                  </ButtonField>
                </div>
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">Source</dt>
                  <dd className="font-medium text-gray-900">
                    {renderEvaluationSource(evaluation.platform)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Total Checkpoints</dt>
                  <dd className="font-medium text-gray-900">
                    {evaluation.totalCheckpoints}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      ) : (
        <div
          className={
            hideHeader
              ? 'cg-ag-grid-field ag-theme-balham w-full'
              : 'mt-4 cg-ag-grid-field ag-theme-balham w-full'
          }
        >
          <AgGridReact
            animateRows={false}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            rowData={evaluations}
          />
        </div>
      )}

      {hasNextPage ? (
        <div className="flex justify-center pt-2">
          <ButtonField mode="ghost" size="small">
            <button
              type="button"
              disabled={isFetchingNextPage}
              onClick={onLoadMore}
            >
              {isFetchingNextPage ? 'Loading...' : 'Load more'}
            </button>
          </ButtonField>
        </div>
      ) : null}
    </section>
  );
};

export default SiteDetailHistory;